import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import AiClientThreadModal from './AiClientThreadModal';
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPES,
  channelMeta,
  countUnreadNotifications,
  createCoreNotificationTemplate,
  deleteCoreNotification,
  deleteCoreNotificationTemplate,
  emitCoreNotification,
  listCoreNotifications,
  listCoreNotificationTemplates,
  markAllNotificationsRead,
  markNotificationRead,
  notificationStatusMeta,
  notificationTypeMeta,
  updateCoreNotificationTemplate,
  type CoreNotification,
  type CoreNotificationTemplate,
  type NotificationChannel,
} from '@/lib/coreNotification';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface NForm {
  type: string;
  channel: NotificationChannel;
  title: string;
  body: string;
  recipient: string;
}
const EMPTY: NForm = { type: 'system', channel: 'in_app', title: '', body: '', recipient: '' };

export default function NotificationsTab({ org }: Props) {
  const [items, setItems] = useState<CoreNotification[]>([]);
  const [templates, setTemplates] = useState<CoreNotificationTemplate[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ type: 'system', channel: 'in_app' as NotificationChannel, title: '', body: '' });
  const [aiClientId, setAiClientId] = useState<number | null>(null);
  const [aiContext, setAiContext] = useState<string | null>(null);
  const [aiOffer, setAiOffer] = useState<{ itemName: string | null; itemType: string | null }>({ itemName: null, itemType: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [notifs, unreadCount, tpls] = await Promise.all([
        listCoreNotifications(org.id),
        countUnreadNotifications(org.id),
        listCoreNotificationTemplates(org.id),
      ]);
      setItems(notifs);
      setUnread(unreadCount);
      setTemplates(tpls);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((n) => n.status === filter)),
    [items, filter],
  );

  const submit = async () => {
    const typeMeta = notificationTypeMeta(form.type);
    const title = form.title.trim() || typeMeta.defaultTitle;
    if (!title) return;
    setSaving(true);
    try {
      await emitCoreNotification({
        tenantId: org.tenant_id,
        organizationId: org.id,
        channel: form.channel,
        type: form.type,
        title,
        body: form.body.trim() || typeMeta.defaultBody,
        recipient: form.recipient.trim() || null,
      });
      setOpen(false);
      setForm(EMPTY);
      load();
    } finally {
      setSaving(false);
    }
  };

  const read = async (n: CoreNotification) => {
    if (n.status === 'read') return;
    setBusyId(n.id);
    try { await markNotificationRead(n.id); load(); } finally { setBusyId(null); }
  };

  const remove = async (n: CoreNotification) => {
    setBusyId(n.id);
    try { await deleteCoreNotification(n.id); load(); } finally { setBusyId(null); }
  };

  const markAll = async () => {
    await markAllNotificationsRead(org.id);
    load();
  };

  // Ouvre la demande du client liée à CETTE offre uniquement.
  // La vue est volontairement ciblée : le marchand ne voit pas le reste de la
  // conversation privée du client avec l'assistant.
  const openAiThread = (n: CoreNotification) => {
    const clientId = Number(n.entity_id);
    if (!Number.isFinite(clientId)) return;
    const meta = (n.metadata || {}) as { query?: string | null; itemName?: string | null; itemType?: string | null };
    const query = (meta.query && String(meta.query).trim()) || '';
    setAiContext(query || n.body || null);
    setAiOffer({
      itemName: meta.itemName ? String(meta.itemName) : null,
      itemType: meta.itemType ? String(meta.itemType) : null,
    });
    setAiClientId(clientId);
    if (n.status !== 'read') markNotificationRead(n.id).then(load);
  };

  const saveTemplate = async () => {
    if (!tplForm.title.trim() && !tplForm.body.trim()) return;
    setSaving(true);
    try {
      await createCoreNotificationTemplate({
        tenantId: org.tenant_id,
        organizationId: org.id,
        type: tplForm.type,
        channel: tplForm.channel,
        label: notificationTypeMeta(tplForm.type).label,
        titleTemplate: tplForm.title.trim() || null,
        bodyTemplate: tplForm.body.trim() || null,
      });
      setTplOpen(false);
      setTplForm({ type: 'system', channel: 'in_app', title: '', body: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Notification Core : un système commun où chaque module <span className="font-semibold text-foreground-700">émet</span> ses messages
          (réservation confirmée, rappel de rendez-vous, facture en retard, commande expédiée…) sur le canal choisi.
        </p>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button onClick={markAll} className="px-3.5 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
              Tout marquer lu
            </button>
          )}
          <button onClick={() => { setForm(EMPTY); setOpen(true); }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i> Émettre
          </button>
        </div>
      </div>

      {/* Canaux */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {NOTIFICATION_CHANNELS.map((c) => {
          const count = items.filter((n) => n.channel === c.key).length;
          return (
            <div key={c.key} className={`rounded-lg border p-3 ${c.available ? 'bg-background-50 border-background-200/70' : 'bg-background-100/60 border-background-200/50'}`}>
              <div className="flex items-center justify-between">
                <i className={`${c.icon} text-lg ${c.available ? 'text-primary-600' : 'text-foreground-300'}`}></i>
                <span className="text-sm font-bold text-foreground-900">{count}</span>
              </div>
              <p className="text-xs font-medium text-foreground-700 mt-1.5">{c.label}</p>
              {!c.available && <p className="text-[10px] text-foreground-400">Intégration à venir</p>}
            </div>
          );
        })}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {['all', 'pending', 'sent', 'read', 'failed'].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${filter === s ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            {s === 'all' ? 'Toutes' : notificationStatusMeta(s).label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-notification-3-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune notification.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const typeMeta = notificationTypeMeta(n.type);
            const chMeta = channelMeta(n.channel);
            const stMeta = notificationStatusMeta(n.status);
            return (
              <div key={n.id} className={`flex items-start gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3 ${n.status === 'pending' ? 'border-l-2 border-l-primary-400' : ''}`}>
                <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0"><i className={`${typeMeta.icon} text-foreground-600`}></i></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-900">{n.title}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${stMeta.className}`}>{stMeta.label}</span>
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap"><i className={chMeta.icon}></i>{chMeta.label}</span>
                  </div>
                  {n.body && <p className="text-xs text-foreground-600 mt-1 line-clamp-2">{n.body}</p>}
                  <p className="text-[11px] text-foreground-400 mt-1">
                    {typeMeta.label}
                    {n.recipient ? ` · ${n.recipient}` : ''}
                    {n.created_at ? ` · ${new Date(n.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {(n.entity_type === 'ai_chat' || n.type === 'ai_customer_search') && (
                    <button onClick={() => openAiThread(n)} title="Voir la demande liée à mon offre" className="w-8 h-8 flex items-center justify-center rounded-full text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-chat-forward-line"></i></button>
                  )}
                  {n.status !== 'read' && (
                    <button onClick={() => read(n)} disabled={busyId === n.id} title="Marquer comme lue" className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-check-double-line"></i></button>
                  )}
                  <button onClick={() => remove(n)} disabled={busyId === n.id} title="Supprimer" className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modèles */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-file-text-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Modèles de messages</h4>
          </div>
          <button onClick={() => setTplOpen(true)} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
            <i className="ri-add-line"></i> Nouveau modèle
          </button>
        </div>
        {templates.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun modèle. Créez-en pour réutiliser vos messages types.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                <i className={`${channelMeta(t.channel).icon} text-foreground-500`}></i>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-800 truncate">{t.label || notificationTypeMeta(t.type).label}</p>
                  <p className="text-xs text-foreground-500 truncate">{t.title_template || '—'}</p>
                </div>
                <button
                  onClick={() => updateCoreNotificationTemplate(t.id, { enabled: !t.enabled }).then(load)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${t.enabled ? 'bg-primary-500' : 'bg-background-200'}`}
                  title={t.enabled ? 'Désactiver' : 'Activer'}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${t.enabled ? 'left-[22px]' : 'left-0.5'}`}></span>
                </button>
                <button onClick={() => deleteCoreNotificationTemplate(t.id).then(load)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"><i className="ri-delete-bin-line"></i></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal émission */}
      {open && (
        <Modal
          title="Émettre une notification"
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>} Émettre
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type</span>
                <select className={INPUT} value={form.type} onChange={(e) => { const m = notificationTypeMeta(e.target.value); setForm({ ...form, type: e.target.value, title: form.title || m.defaultTitle, body: form.body || m.defaultBody }); }}>
                  {NOTIFICATION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Canal</span>
                <select className={INPUT} value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value as NotificationChannel })}>
                  {NOTIFICATION_CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}{c.available ? '' : ' (à venir)'}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre</span>
              <input className={INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Réservation confirmée" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Message</span>
              <textarea rows={3} className={`${INPUT} resize-none`} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={500} placeholder="Contenu de la notification…" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Destinataire (optionnel)</span>
              <input className={INPUT} value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} placeholder="Email ou numéro" />
            </label>
          </div>
        </Modal>
      )}

      {/* Modal modèle */}
      {tplOpen && (
        <Modal
          title="Nouveau modèle de message"
          onClose={() => setTplOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setTplOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={saveTemplate} disabled={saving} className="px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type</span>
                <select className={INPUT} value={tplForm.type} onChange={(e) => setTplForm({ ...tplForm, type: e.target.value })}>
                  {NOTIFICATION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Canal</span>
                <select className={INPUT} value={tplForm.channel} onChange={(e) => setTplForm({ ...tplForm, channel: e.target.value as NotificationChannel })}>
                  {NOTIFICATION_CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre type</span>
              <input className={INPUT} value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} placeholder="Ex : Votre commande est en route" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Corps type</span>
              <textarea rows={3} className={`${INPUT} resize-none`} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} maxLength={500} placeholder="Bonjour {{client}}, votre commande {{num}} a été expédiée." />
            </label>
          </div>
        </Modal>
      )}

      {/* Conversation IA complète du client (ouverte depuis la notification) */}
      {aiClientId !== null && (
        <AiClientThreadModal
          organizationId={org.id}
          tenantId={org.tenant_id}
          clientId={aiClientId}
          contextLabel={aiContext}
          mode="scoped"
          itemName={aiOffer.itemName}
          itemType={aiOffer.itemType}
          onClose={() => { setAiClientId(null); setAiOffer({ itemName: null, itemType: null }); }}
        />
      )}
    </div>
  );
}